/**
 * Selection & move overlay — draws the Excel-style range rectangle, the rings
 * around multi-selected events and, in "verschieben" mode, the ghost preview
 * of every picked event at its target position (tinted red when the placement
 * would be invalid). Pure overlay: event blocks never re-render during
 * selection or move.
 */
import React from "react";
import { MCEvent, MCGridConfig, MCInteractionState, MCResource } from "../model/types";
import { MCEventIndex } from "../model/eventIndex";
import { planMove, rangeRect } from "../model/selection";
import { minutesToTime, yForMin } from "../model/gridGeometry";
import { MiniStore, useMiniSelector } from "../model/miniStore";

interface SelectionOverlayProps {
  cfg: MCGridConfig;
  store: MiniStore<MCInteractionState>;
  index: MCEventIndex;
  resources: ReadonlyArray<MCResource>;
  resourceIndexById: ReadonlyMap<string, number>;
}

const eventRectStyle = (
  cfg: MCGridConfig,
  event: MCEvent,
  colIndex: number,
): React.CSSProperties => {
  const laneFraction = 1 / event.laneCount;
  const top = yForMin(cfg, event.startMin);
  return {
    left: `calc(var(--mc-col-w) * ${colIndex + event.lane * laneFraction})`,
    width: `calc(var(--mc-col-w) * ${laneFraction} - 2px)`,
    top,
    height: Math.max(yForMin(cfg, event.endMin) - top - 1, cfg.slotHeightPx - 1),
  };
};

const SelectionOverlay: React.FC<SelectionOverlayProps> = ({
  cfg,
  store,
  index,
  resources,
  resourceIndexById,
}) => {
  const range = useMiniSelector(store, (s) => s.range);
  const multiSelectedIds = useMiniSelector(store, (s) => s.multiSelectedIds);
  const move = useMiniSelector(store, (s) => s.move);

  const children: React.ReactNode[] = [];

  // ── Range rectangle (marquee / shift-extended) ───────────────────────────
  if (range && !move) {
    const rect = rangeRect(range, cfg);
    const top = yForMin(cfg, rect.minStart);
    children.push(
      <div
        key="range"
        className="mc-sel-rect"
        style={{
          left: `calc(var(--mc-col-w) * ${rect.colStart})`,
          width: `calc(var(--mc-col-w) * ${rect.colEnd - rect.colStart + 1})`,
          top,
          height: yForMin(cfg, rect.minEnd) - top,
        }}
      />,
    );
  }

  // ── Rings around every multi-selected event ──────────────────────────────
  if (multiSelectedIds.size > 0 && !move) {
    multiSelectedIds.forEach((id) => {
      const event = index.byId.get(id);
      if (!event) return;
      const colIndex = resourceIndexById.get(event.resourceId);
      if (colIndex == null) return;
      children.push(
        <div
          key={`multi-${id}`}
          className="mc-ring mc-ring--multi"
          style={eventRectStyle(cfg, event, colIndex)}
        />,
      );
    });
  }

  // ── Move ghosts (picked-up events at their target positions) ─────────────
  // Rendered from the transaction's CAPTURED sources — authoritative snapshot
  // rebuilds mid-move re-derive the grid underneath, but can never blank or
  // displace the active ghosts.
  if (move) {
    const plan = planMove(index, resources, move.sources, move.deltaCols, move.deltaSlots, cfg);
    for (const m of plan.moves) {
      const sourceCol = resourceIndexById.get(m.event.resourceId);
      if (sourceCol == null) continue;
      const top = yForMin(cfg, m.targetStartMin);
      const height = Math.max(
        yForMin(cfg, m.targetEndMin) - top - 1,
        cfg.slotHeightPx - 1,
      );
      // Ghosts keep each event's lane footprint so the cluster shape is
      // preserved at the target and overlapping ghosts never overprint.
      const laneFraction = 1 / m.event.laneCount;
      const targetCol = sourceCol + move.deltaCols;
      children.push(
        <div
          key={`src-${m.event.id}`}
          className="mc-move-source"
          style={eventRectStyle(cfg, m.event, sourceCol)}
        />,
        <div
          key={`ghost-${m.event.id}`}
          className={`mc-ghost${plan.valid ? "" : " is-invalid"}`}
          style={{
            left: `calc(var(--mc-col-w) * ${targetCol + m.event.lane * laneFraction} + 1px)`,
            width: `calc(var(--mc-col-w) * ${laneFraction} - 3px)`,
            top,
            height,
          }}
        >
          <span className="mc-ghost-time">
            {minutesToTime(m.targetStartMin)} {m.event.patientLabel ?? m.event.title}
          </span>
        </div>,
      );
    }
    // Hint chip near the first ghost
    if (plan.moves.length > 0) {
      const first = plan.moves[0];
      const firstCol = (resourceIndexById.get(first.event.resourceId) ?? 0) + move.deltaCols;
      children.push(
        <div
          key="move-hint"
          className="mc-move-hint"
          style={{
            left: `calc(var(--mc-col-w) * ${firstCol})`,
            top: Math.max(yForMin(cfg, first.targetStartMin) - 26, 0),
          }}
        >
          {plan.valid
            ? `${plan.moves.length} Termin${plan.moves.length === 1 ? "" : "e"} — V platzieren · Esc abbrechen`
            : "Ungültige Position"}
        </div>,
      );
    }
  }

  return <>{children}</>;
};

export default React.memo(SelectionOverlay);
